import { BaseApi, HttpMethodEnum } from '../BaseApi/BaseApi';
import { IListItem } from '../SexesApi/SexesApi';

// Types for HCP Delegation
export interface ICreateDelegationModel {
    delegatedHcpID: number;
    delegatedHcpIDs: number[];
    patientID: number;
    accessLevel: AccessLevel;
    reason?: string;
}

export interface IDelegationModel {
    id: number;
    delegatingHcpID: number;
    delegatingHcpName: string;
    delegatedHcpID: number;
    delegatedHcpName: string;
    delegatedHcpSpecialty: number;
    patientID: number;
    patientName: string;
    accessLevel: AccessLevel;
    createdDate: string;
    isActive: boolean;
    patientConsentStatus: ConsentStatus;
    consentDate?: string;
    canRevoke: boolean;
    isNewDelegation: boolean;
    healthcareProfessionalTypes: IListItem[];
}

export interface IAvailableHcpModel {
    id: number;
    name: string;
    email: string;
    specialty: number;
    licenseNumber: string;
    isAvailableForDelegation: boolean;
    belongsToCurrentFacilities: boolean;
    healthcareProfessionalTypes: IListItem[];
}   

export enum AccessLevel {
    ReadOnly = 1,
    ReadWrite = 2
}

export enum ConsentStatus {
    Pending = 1,
    Granted = 2,
    Revoked = 3,
    Expired = 4
}

export interface IApiResponse<T> {
    success: boolean;
    message?: string;
    delegations?: T;
    availableHcps?: T;
}

export class HcpDelegationApi extends BaseApi {
    private static baseApiUrl: string = `${BaseApi.apiUrl}/HcpDelegation`;

    public static apiUrls = {
        createDelegation: `${HcpDelegationApi.baseApiUrl}/Create`,
        getPatientDelegations: `${HcpDelegationApi.baseApiUrl}/Patient/:id`,
        getMyDelegations: `${HcpDelegationApi.baseApiUrl}/MyDelegations`,
        revokeDelegation: `${HcpDelegationApi.baseApiUrl}/:id`,
        getAvailableHcps: `${HcpDelegationApi.baseApiUrl}/AvailableHcps`,
    };

    // HCP Methods
    public static async createDelegation(request: ICreateDelegationModel): Promise<IApiResponse<IDelegationModel[]>> {
        return this.sendApiRequest<IApiResponse<IDelegationModel[]>>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.createDelegation,
            data: request as any,
        });
    }

    public static async getMyDelegations(): Promise<IApiResponse<IDelegationModel[]>> {
        return this.sendApiRequest<IApiResponse<IDelegationModel[]>>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.getMyDelegations,
        });
    }

    public static async getAvailableHcps(): Promise<IApiResponse<IAvailableHcpModel[]>> {
        return this.sendApiRequest<IApiResponse<IAvailableHcpModel[]>>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.getAvailableHcps,
        });
    }

    // Shared Methods - Patient delegations can be viewed by both HCPs and Patients
    public static async getPatientDelegations(patientId: number): Promise<IApiResponse<IDelegationModel[]>> {
        return this.sendApiRequest<IApiResponse<IDelegationModel[]>>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.getPatientDelegations,
            id: patientId.toString(),
        });
    }

    // Shared Methods
    public static async revokeDelegation(delegationId: number, reason?: string): Promise<IApiResponse<void>> {
        return this.sendApiRequest<IApiResponse<void>>({
            method: HttpMethodEnum.DELETE,
            path: this.apiUrls.revokeDelegation,
            id: delegationId.toString(),
            data: reason ? { reason } as any : undefined,
        });
    }
}
